import React, { useState, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useEnvironment } from '../contexts/EnvironmentContext'
import { useSupportCount } from '../hooks/useSupportCount'
import { useKeyboardShortcuts, KeyboardShortcutsHelp } from '../hooks/useKeyboard.jsx'
import { Sheet, SheetContent } from './ui/sheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import CommandPalette from './admin/CommandPalette'
import Breadcrumbs from './admin/Breadcrumbs'
import { 
  LayoutDashboard, Users, Trophy, DollarSign, Shield, LogOut, Menu, Bell, 
  MessageCircle, Keyboard, Settings2, Mail, Calendar, BarChart3, 
  ChevronDown, ChevronRight, ChevronLeft, Monitor, Server, Code2, Search,
  PanelLeftClose, PanelLeft, Activity
} from 'lucide-react'

const navigation = [
  { name: 'Dashboard', href: '', icon: LayoutDashboard, shortcut: '⌘D' },
  { name: 'Users', href: 'users', icon: Users, shortcut: '⌘U' },
  { name: 'Teams', href: 'teams', icon: Trophy, shortcut: '⌘T' },
  { name: 'Analytics', href: 'analytics', icon: BarChart3 },
  { name: 'Financials', href: 'payments', icon: DollarSign, shortcut: '⌘F' },
  { name: 'Support', href: 'support', icon: MessageCircle, shortcut: '⌘S', badge: true },
  { name: 'Communications', href: 'communications', icon: Mail, shortcut: '⌘C' },
  { name: 'Operations', href: 'operations', icon: Activity },
  { name: 'Settings', href: 'settings', icon: Settings2 },
]

export default function Layout() {
  const { user, logout } = useAuth()
  const { environment, switchEnvironment } = useEnvironment()
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [commandOpen, setCommandOpen] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState(['Financials'])
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === 'true' } catch { return false }
  })
  const { totalUnread } = useSupportCount()
  
  useKeyboardShortcuts()

  // ⌘K listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCommandOpen(prev => !prev)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])
  
  useEffect(() => {
    const handleShowShortcuts = () => setShowShortcuts(true)
    document.addEventListener('show-shortcuts', handleShowShortcuts)
    return () => document.removeEventListener('show-shortcuts', handleShowShortcuts)
  }, [])

  useEffect(() => {
    try { localStorage.setItem('sidebar-collapsed', String(collapsed)) } catch {}
  }, [collapsed])

  const toggleMenu = (name) => {
    setExpandedMenus(prev => 
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    )
  }

  const NavItem = ({ item, mobile = false }) => {
    const pathSegment = location.pathname.split('/').pop() || ''
    const isActive = item.href === '' 
      ? (location.pathname === '/' || location.pathname === '' || pathSegment === '')
      : pathSegment === item.href || location.pathname.endsWith('/' + item.href)
    
    const hasActiveChild = item.children?.some(child => 
      pathSegment === child.href || location.pathname.endsWith('/' + child.href)
    )

    // Collapsed icon-only mode (desktop only)
    if (collapsed && !mobile && !item.children) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Link
              to={item.href || '/'}
              className={`flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-all duration-200 ${
                isActive 
                  ? 'bg-indigo-50 text-indigo-600' 
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.badge && totalUnread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-4 h-4 flex items-center justify-center text-[10px] font-bold rounded-full px-1 bg-red-500 text-white">
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{item.name}</p>
          </TooltipContent>
        </Tooltip>
      )
    }
    
    if (item.children) {
      const isExpanded = expandedMenus.includes(item.name)
      return (
        <div>
          <button
            onClick={() => toggleMenu(item.name)}
            className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 group ${
              hasActiveChild 
                ? 'bg-indigo-50 text-indigo-600' 
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-3">
              <item.icon className="w-5 h-5" />
              <span className="font-medium text-sm">{item.name}</span>
            </div>
            <div className="flex items-center gap-2">
              {item.shortcut && !mobile && (
                <span className="text-xs opacity-0 group-hover:opacity-60 transition-opacity text-gray-400">
                  {item.shortcut}
                </span>
              )}
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </div>
          </button>
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-0.5">
              {item.children.map(child => (
                <Link
                  key={child.href}
                  to={child.href || '/'}
                  onClick={mobile ? () => setMobileOpen(false) : undefined}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 ${
                    pathSegment === child.href || location.pathname.endsWith('/' + child.href)
                      ? 'bg-indigo-50 text-indigo-600' 
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <child.icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{child.name}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )
    }
    
    return (
      <Link
        to={item.href || '/'}
        onClick={mobile ? () => setMobileOpen(false) : undefined}
        className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 group ${
          isActive 
            ? 'bg-indigo-50 text-indigo-600 font-semibold' 
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        <div className="flex items-center gap-3">
          <item.icon className="w-5 h-5" />
          <span className="font-medium text-sm">{item.name}</span>
        </div>
        <div className="flex items-center gap-2">
          {item.badge && totalUnread > 0 && (
            <span className="min-w-5 h-5 flex items-center justify-center text-xs font-bold rounded-full px-1.5 bg-red-500 text-white">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
          {item.shortcut && !mobile && (
            <span className={`text-xs opacity-0 group-hover:opacity-60 transition-opacity text-gray-400`}>
              {item.shortcut}
            </span>
          )}
        </div>
      </Link>
    )
  }

  const SidebarContent = ({ mobile = false }) => (
    <>
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-gray-200 ${collapsed && !mobile ? 'justify-center px-2' : 'px-6'}`}>
        {collapsed && !mobile ? (
          <img src="/squadline-logo.png" alt="Squadline" className="h-8 w-8 object-contain" />
        ) : (
          <img src="/squadline-wordmark-white.png" alt="Squadline" className="h-7 w-auto brightness-0" />
        )}
      </div>
      
      {/* Environment Indicator */}
      {(!collapsed || mobile) && (
        <div className="px-4 py-3 border-b border-gray-200">
          <div className={`flex items-center justify-between p-3 rounded-lg ${
            environment === 'production' 
              ? 'bg-red-50 border border-red-200' 
              : 'bg-amber-50 border border-amber-200'
          }`}>
            <div className="flex items-center gap-2">
              {environment === 'production' ? (
                <Server className="w-4 h-4 text-red-500" />
              ) : (
                <Monitor className="w-4 h-4 text-amber-500" />
              )}
              <span className={`text-sm font-semibold ${
                environment === 'production' ? 'text-red-600' : 'text-amber-600'
              }`}>
                {environment === 'production' ? 'Production' : 'Development'}
              </span>
            </div>
            <button
              onClick={() => switchEnvironment(environment === 'production' ? 'development' : 'production')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                environment === 'production'
                  ? 'bg-red-100 text-red-600 hover:bg-red-200'
                  : 'bg-amber-100 text-amber-600 hover:bg-amber-200'
              }`}
            >
              Switch
            </button>
          </div>
        </div>
      )}

      {/* Collapsed env dot */}
      {collapsed && !mobile && (
        <div className="flex justify-center py-3 border-b border-gray-200">
          <Tooltip delayDuration={0}>
            <TooltipTrigger>
              <div className={`w-3 h-3 rounded-full ${environment === 'production' ? 'bg-red-500' : 'bg-amber-500'}`} />
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{environment === 'production' ? 'Production' : 'Development'}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      )}
      
      {/* Navigation */}
      <nav className={`flex-1 p-3 space-y-1 overflow-y-auto ${collapsed && !mobile ? 'px-2' : ''}`}>
        {navigation.map((item) => (
          <NavItem key={item.name} item={item} mobile={mobile} />
        ))}
      </nav>
      
      {/* User section */}
      <div className="p-4 border-t border-gray-200">
        {(!collapsed || mobile) && (
          <>
            <button
              onClick={() => setShowShortcuts(true)}
              className="flex items-center gap-2 w-full px-4 py-2 mb-3 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Keyboard className="w-4 h-4" />
              <span>Keyboard shortcuts</span>
              <kbd className="ml-auto px-1.5 py-0.5 text-xs bg-gray-100 border border-gray-200 rounded">?</kbd>
            </button>
            
            <div className="flex items-center gap-3 p-3 mb-3 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <span className="text-indigo-600 font-semibold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </span>
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </>
        )}

        {collapsed && !mobile && (
          <div className="flex flex-col items-center gap-2">
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-indigo-600 font-semibold text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{user?.name}</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button onClick={logout} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                  <LogOut className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right"><p>Sign Out</p></TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(c => !c)}
          className={`mt-3 flex items-center gap-2 w-full py-2 text-sm text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors ${collapsed && !mobile ? 'justify-center px-2' : 'px-4'}`}
        >
          {collapsed && !mobile ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          {(!collapsed || mobile) && <span>Collapse</span>}
        </button>
      </div>
    </>
  )

  const sidebarWidth = collapsed ? 'w-[72px]' : 'w-72'
  const sidebarPl = collapsed ? 'lg:pl-[72px]' : 'lg:pl-72'

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gray-50">
        <KeyboardShortcutsHelp isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
        <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
        
        {/* Mobile sidebar using Sheet */}
        <div className="lg:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent side="left" className="p-0 w-72 flex flex-col">
              <SidebarContent mobile />
            </SheetContent>
          </Sheet>
        </div>

        {/* Desktop sidebar */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:${sidebarWidth} lg:flex-col transition-all duration-200`}>
          <div className="flex flex-col flex-1 bg-white border-r border-gray-200">
            <SidebarContent />
          </div>
        </div>

        {/* Main content */}
        <div className={`${sidebarPl} transition-all duration-200`}>
          {/* Sticky top header */}
          <div className="sticky top-0 z-40 flex items-center justify-between h-14 px-4 lg:px-8 bg-white border-b border-gray-200">
            {/* Left: mobile hamburger + breadcrumbs */}
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setMobileOpen(true)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
              >
                <Menu className="w-5 h-5 text-gray-700" />
              </button>
              <div className="hidden sm:block">
                <Breadcrumbs />
              </div>
            </div>
            
            {/* Right: actions */}
            <div className="flex items-center gap-2">
              {/* ⌘K hint */}
              <button
                onClick={() => setCommandOpen(true)}
                className="hidden sm:flex items-center gap-1.5 text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded border border-gray-200 hover:bg-gray-200 transition-colors"
              >
                <Search className="w-3 h-3" />
                <span>Search</span>
                <kbd className="ml-1 text-[10px] text-gray-400">⌘K</kbd>
              </button>

              {/* Notification bell */}
              {totalUnread > 0 && (
                <Link 
                  to="/support"
                  className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <Bell className="w-5 h-5 text-gray-600" />
                  <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 flex items-center justify-center text-[10px] font-bold rounded-full px-1 bg-red-500 text-white">
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </span>
                </Link>
              )}
              {totalUnread === 0 && (
                <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                  <Bell className="w-5 h-5 text-gray-400" />
                </button>
              )}

              {/* User dropdown */}
              <div className="hidden lg:flex items-center gap-2 ml-2 pl-2 border-l border-gray-200">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-indigo-600 font-semibold text-xs">
                    {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-700">{user?.name?.split(' ')[0]}</span>
              </div>
            </div>
          </div>
          
          {/* Page content */}
          <main className="p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  )
}
